/**
 * StockOrder Review Page
 *
 * This page allows users to review the details of an stock_order and add notes.
 *
 * The stock_orderId param is required.
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useSuspenseQuery } from "@tanstack/react-query";
import { suspenseOptions } from "@/lib/suspense-query";
import { getGetStockOrderQueryOptions } from "@/requests/gen/react-query/stock-order";
import StockOrderConfirmCard from "./-stock-order-confirm-card";
import StockOrderContentsCard from "./-stock-order-contents-card";
import StockOrderHistory from "./-stock-order-history";
import { StockOrderStatusEnum } from "@/requests/gen/react-query/fastAPI.schemas";
import { StockOrderCrumbs } from "../-crumbs";
import { StockOrderActions } from "./-stock-order-actions";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute(
  "/_private/_stock-360/stock-order/$stockOrderId/",
)({
  loader: ({ context: { queryClient }, params: { stockOrderId } }) =>
    queryClient.ensureQueryData(getGetStockOrderQueryOptions(stockOrderId)),
  component: StockOrderReviewPage,
});

function StockOrderReviewPage() {
  const { stockOrderId } = Route.useParams();
  // Fetch the stock_order data
  const { data: stock_order, refetch } = useSuspenseQuery(
    suspenseOptions(getGetStockOrderQueryOptions(stockOrderId)),
  );

  const canEdit =
    stock_order?.status === StockOrderStatusEnum.Draft ||
    stock_order?.status === StockOrderStatusEnum.Review;

  return (
    <div>
      <title>{`Review Stock Order ${stockOrderId} - Legrande Health Portal`}</title>
      <div className="flex items-center justify-between pr-4">
        <StockOrderCrumbs canEdit={canEdit} />
        {stock_order && (
          <StockOrderActions stock_order={stock_order} refetch={refetch} />
        )}
      </div>
      {/* Main Content */}

      <main className="grid flex-1 items-start gap-4 xl:grid-cols-3">
        {/* Big Column */}
        <div className="grid gap-4 xl:col-span-2">
          {/* Contents Section */}
          <div className="relative">
            <StockOrderContentsCard
              stock_order={stock_order}
              canEdit={canEdit}
              refetch={refetch}
            />
          </div>

          <Card className="gap-2">
            <CardHeader>
              <CardTitle className="text-base">History</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <StockOrderHistory stock_order={stock_order} />
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-4">
          {/* Confirm Section */}
          {stock_order && (
            <div className="relative">
              <StockOrderConfirmCard
                stock_order={stock_order}
                refetch={refetch}
              />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
